'use client';

// The one place that knows how to subscribe to a plan or to a `service`'s "subscribe and save"
// tiers: tier picker, payment-mode picker (monthly / whole term upfront), login gate, and the
// actual POST /subscriptions/checkout call. Rendered from two different host surfaces —
// ProductBlocks.tsx's page-builder "product-add-to-cart" block (styled via that block's own
// settings) and the plain fallback product page (product-details.tsx via add-to-cart-button.tsx)
// — on purpose kept as one component so the two never drift apart the way the plain add-to-cart
// buttons already had (see the 2026-09-22 add-to-cart-surface audit).

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { toast } from '@/core/utils/toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { cn } from '@/core/utils/cn';
import { fetchAPI } from '@/core/api/client';
import { formatPrice } from '@/core/utils/currency';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { LoginGateModal } from '@/modules/shop/components/negotiation/login-gate-modal';
import { activeSubscriptionTiers, type SubscriptionTier } from '@/core/types/subscription-tiers';

const BASE_OPTION_ID = '__base__';

interface Props {
    product: { id: number; product_type?: string; service_config?: any };
    /** Page-builder override (ProductAddToCartEditor's "Tekst przycisku „Wykup abonament"") — falls
     *  back to the translated default when the host doesn't pass one (e.g. the fallback page). */
    subscribeButtonText?: string;
    /** Lets a Builder-styled host (ProductBlocks.tsx) make the subscribe button match its own
     *  "Dodaj do koszyka"/"Kup teraz" styling instead of the plain shadcn default. */
    buttonStyle?: React.CSSProperties;
    buttonClassName?: string;
    className?: string;
}

function optionButtonClass(selected: boolean) {
    return cn(
        'px-3 py-2 rounded-lg border text-xs sm:text-sm font-semibold text-left transition-colors',
        selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted border-input',
    );
}

export function SubscriptionPurchasePanel({ product, subscribeButtonText, buttonStyle, buttonClassName, className }: Props) {
    const { t } = useTranslation();
    const { data: session } = useSession();

    const tiers = activeSubscriptionTiers(product.service_config);
    // A subscription_plan has its own base, no-commitment price; a `service`'s "no commitment"
    // equivalent is the ordinary one-off purchase button already next to this panel, not an
    // option inside it — a service has nothing recurring to fall back to on its own.
    const hasBaseOption = product.product_type === 'subscription_plan' && product.service_config?.fixed_price != null;

    const [selectedId, setSelectedId] = useState<string>(() => (hasBaseOption ? BASE_OPTION_ID : tiers[0]?.id ?? ''));
    const [billingMode, setBillingMode] = useState<'monthly' | 'upfront'>('monthly');
    const [showLoginGate, setShowLoginGate] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const selectedTier: SubscriptionTier | null = selectedId === BASE_OPTION_ID ? null : tiers.find(tr => tr.id === selectedId) ?? null;

    // If the newly selected tier doesn't offer the currently chosen mode, fall back to whichever
    // one it does — never leave the radio pointing at a mode that would 400 at checkout.
    useEffect(() => {
        if (!selectedTier) return;
        if (billingMode === 'monthly' && !selectedTier.allow_monthly_billing) setBillingMode('upfront');
        if (billingMode === 'upfront' && !selectedTier.allow_upfront_billing) setBillingMode('monthly');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTier?.id]);

    if (!hasBaseOption && tiers.length === 0) return null;

    const showModeChoice = !!selectedTier && selectedTier.allow_monthly_billing && selectedTier.allow_upfront_billing;

    const subscribe = async () => {
        if (!session?.user) {
            setShowLoginGate(true);
            return;
        }
        setSubmitting(true);
        try {
            const res: any = await fetchAPI('/subscriptions/checkout', {
                method: 'POST',
                body: JSON.stringify({
                    plan_product_id: product.id,
                    ...(selectedTier ? { tier_id: selectedTier.id, billing_mode: billingMode } : {}),
                }),
            });
            if (res?.checkout_url) {
                window.location.href = res.checkout_url;
                return; // leaving the page — keep the button busy
            }
            toast.error(t('product.subscribe_error', 'Nie udało się rozpocząć subskrypcji. Spróbuj ponownie później.'));
        } catch (e: any) {
            toast.error(e?.message || t('product.subscribe_error', 'Nie udało się rozpocząć subskrypcji. Spróbuj ponownie później.'));
        }
        setSubmitting(false);
    };

    return (
        <div className={cn('space-y-3', className)}>
            {!showLoginGate ? null : (
                <LoginGateModal
                    isOpen={showLoginGate}
                    onClose={() => setShowLoginGate(false)}
                    title={t('product.subscribe_login_title', 'Zaloguj się, aby wykupić abonament')}
                    message={t('product.subscribe_login_message', 'Abonament jest przypisany do Twojego konta — zaloguj się lub załóż bezpłatne konto.')}
                />
            )}

            {(hasBaseOption || tiers.length > 1) && (
                <div className="space-y-1.5" role="radiogroup" aria-label={t('product.subscription_tier_label', 'Okres zobowiązania')}>
                    {hasBaseOption && (
                        <button type="button" role="radio" aria-checked={selectedId === BASE_OPTION_ID}
                            onClick={() => setSelectedId(BASE_OPTION_ID)} className={cn('w-full', optionButtonClass(selectedId === BASE_OPTION_ID))}>
                            {t('product.subscription_no_commitment', 'Bez zobowiązania')} — {formatPrice(product.service_config.fixed_price, 'PLN')}
                            {t('product.per_month', '/ mies.')}
                        </button>
                    )}
                    {tiers.map(tier => (
                        <button key={tier.id} type="button" role="radio" aria-checked={selectedId === tier.id}
                            onClick={() => setSelectedId(tier.id)} className={cn('w-full', optionButtonClass(selectedId === tier.id))}>
                            {tier.label} — {formatPrice(tier.monthly_price, 'PLN')}{t('product.per_month', '/ mies.')}
                        </button>
                    ))}
                </div>
            )}

            {showModeChoice && selectedTier && (
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('product.subscription_billing_mode_label', 'Sposób płatności')}>
                    <button type="button" role="radio" aria-checked={billingMode === 'monthly'}
                        onClick={() => setBillingMode('monthly')} className={optionButtonClass(billingMode === 'monthly')}>
                        {t('product.billing_mode_monthly', 'Co miesiąc')}
                    </button>
                    <button type="button" role="radio" aria-checked={billingMode === 'upfront'}
                        onClick={() => setBillingMode('upfront')} className={optionButtonClass(billingMode === 'upfront')}>
                        {t('product.billing_mode_upfront', 'Z góry za cały okres')} — {formatPrice(selectedTier.monthly_price * selectedTier.months, 'PLN')}
                    </button>
                </div>
            )}

            <Button
                type="button"
                style={buttonStyle}
                className={cn('w-full sm:w-auto', buttonClassName)}
                disabled={submitting || (!hasBaseOption && !selectedTier)}
                onClick={subscribe}
            >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {subscribeButtonText?.trim() || t('product.subscribe', 'Wykup abonament')}
            </Button>
        </div>
    );
}
